import { useCallback, useLayoutEffect, useRef } from "react";
import { getScrollRoot, isScrollContainer } from "../../util/getScrollRoot";

export function usePageNumberObserver(
  container: HTMLElement | null,
  props: {
    onPageChange?: (data: { pageNumber: string }) => void;
    onRequestScreenPageNumbers?: (
      elementId: string,
    ) => Array<{ pageNumber: string; dataId?: string }>;
  },
  currentElementId: string,
) {
  // Stable refs for callbacks — avoids recreating sendPageNumber / setupObserver on every render,
  // which would otherwise cause the onViewerMounted effect to re-fire unnecessarily.
  const onPageChangeRef = useRef(props.onPageChange);
  onPageChangeRef.current = props.onPageChange;
  const onRequestScreenPageNumbersRef = useRef(props.onRequestScreenPageNumbers);
  onRequestScreenPageNumbersRef.current = props.onRequestScreenPageNumbers;
  const currentElementIdRef = useRef(currentElementId);
  currentElementIdRef.current = currentElementId;

  const intersectionObserverRef = useRef<IntersectionObserver | null>(null);
  const intersectingPageNumbersRef = useRef<string[]>([]);
  const currentPageNumberRef = useRef("");

  const sendPageNumber = useCallback(() => {
    onPageChangeRef.current?.({
      pageNumber: currentPageNumberRef.current || "-",
    });
  }, []);

  const destroyObserver = useCallback(() => {
    if (intersectionObserverRef.current) {
      intersectionObserverRef.current.disconnect();
      intersectionObserverRef.current = null;
    }
    intersectingPageNumbersRef.current = [];
    currentPageNumberRef.current = "";
  }, []);

  const setupObserver = useCallback(() => {
    // If container is null, return early to avoid unnecessary cleanup.
    if (!onPageChangeRef.current || !container) {
      return;
    }

    destroyObserver();

    const scrollRoot = getScrollRoot(container) ?? container;
    // A non-scrolling root (native WebView/document scroll) never clips its children, so page
    // breaks would stay "intersecting" forever; observe against the viewport instead.
    const useViewportRoot = !isScrollContainer(scrollRoot);

    const pageBreakElements = container.querySelectorAll("[data-page-number]");

    const getScreenStartingPage = (): string => {
      const elemId = currentElementIdRef.current;
      if (!elemId || !onRequestScreenPageNumbersRef.current) return "-";
      const entries = onRequestScreenPageNumbersRef.current(elemId);
      if (!entries?.length) return "-";
      return entries.find((e) => !e.dataId)?.pageNumber ?? "-";
    };

    if (pageBreakElements.length === 0) {
      currentPageNumberRef.current = getScreenStartingPage();
      sendPageNumber();
      return;
    }

    intersectionObserverRef.current = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        let lastIntersecting: IntersectionObserverEntry | undefined;
        for (const e of entries) {
          if (e.isIntersecting) {
            lastIntersecting = e;
          }
        }
        const entry = lastIntersecting ?? entries[0];
        const dataId = entry.target.getAttribute("data-id") as string;

        if (entry.isIntersecting) {
          if (intersectingPageNumbersRef.current.includes(dataId)) {
            intersectingPageNumbersRef.current = intersectingPageNumbersRef.current.filter(
              (id) => id !== dataId,
            );
          }
          intersectingPageNumbersRef.current.push(dataId);

          let intersectingPageNumber: string;

          if (intersectingPageNumbersRef.current.length === 1) {
            intersectingPageNumber = entry.target.getAttribute("data-page-number") as string;
          } else {
            const selector = intersectingPageNumbersRef.current
              .map((id) => `[data-id="${id}"]`)
              .join(",");
            const elements = container.querySelectorAll(selector);
            intersectingPageNumber = elements[elements.length - 1]?.getAttribute(
              "data-page-number",
            ) as string;
          }

          if (intersectingPageNumber && intersectingPageNumber !== currentPageNumberRef.current) {
            currentPageNumberRef.current = intersectingPageNumber;
            sendPageNumber();
          }
        } else if (intersectingPageNumbersRef.current.includes(dataId)) {
          intersectingPageNumbersRef.current = intersectingPageNumbersRef.current.filter(
            (id) => id !== dataId,
          );

          const rootBottom = useViewportRoot ? window.innerHeight : entry.rootBounds?.bottom ?? 0;
          if (entry.boundingClientRect.bottom > rootBottom) {
            const previousPage = entry.target.getAttribute("data-previous-page-number");
            if (previousPage && previousPage !== currentPageNumberRef.current) {
              currentPageNumberRef.current = previousPage;
              sendPageNumber();
            }
          }
        }
      },
      { threshold: 0.75, root: useViewportRoot ? null : scrollRoot },
    );

    pageBreakElements.forEach((el, index) => {
      let prevPageNum: string | null = null;
      if (index === 0) {
        const startingPage = getScreenStartingPage();
        if (startingPage !== "-") prevPageNum = startingPage;
      } else {
        prevPageNum = pageBreakElements[index - 1].getAttribute("data-page-number");
      }
      if (prevPageNum) {
        el.setAttribute("data-previous-page-number", prevPageNum);
      }
      intersectionObserverRef.current?.observe(el);
    });

    const rootBottom = useViewportRoot
      ? window.innerHeight
      : scrollRoot.getBoundingClientRect?.()?.bottom ?? window.innerHeight;
    let initialPage = "";

    for (let i = pageBreakElements.length - 1; i >= 0; i--) {
      const elRect = (pageBreakElements[i] as HTMLElement).getBoundingClientRect();
      if (elRect.top <= rootBottom) {
        initialPage = pageBreakElements[i].getAttribute("data-page-number") ?? "";
        break;
      }
    }

    if (!initialPage) {
      initialPage = getScreenStartingPage();
    }

    currentPageNumberRef.current = initialPage;
    sendPageNumber();
  }, [container, destroyObserver, sendPageNumber]);

  /** IntersectionObserver does not report the target page after a same-screen scroll-to-dataId. */
  const notifyPageByDataId = useCallback(
    (dataId: string) => {
      const pageNumber = container
        ?.querySelector(`[data-id="${dataId}"]`)
        ?.getAttribute("data-page-number");
      if (!pageNumber) return;
      intersectingPageNumbersRef.current = [dataId];
      currentPageNumberRef.current = pageNumber;
      sendPageNumber();
    },
    [container, sendPageNumber],
  );

  useLayoutEffect(() => () => destroyObserver(), [destroyObserver]);

  return { setupObserver, destroyObserver, notifyPageByDataId };
}
